import { Navigate } from "@tanstack/react-router";
import type { FunnelReport } from "@tex/finance-shared";
import { useState } from "react";
import {
  Alert,
  Button,
  Card,
  Field,
  Input,
  Mono,
  PageHeader,
  Select,
} from "../components/ui";
import { errorMessage } from "../lib/api";
import { useDealers } from "../lib/dealers";
import { formatDate, STAGE_LABELS } from "../lib/format";
import {
  CLOSE_REASON_LABELS,
  type CsvDownload,
  downloadCaseEventsCsv,
  localDay,
  REFERRAL_CATEGORY_LABELS,
  type ReportWindowInput,
  useFunnelReport,
} from "../lib/operations";
import { isTexStaff, useMe } from "../lib/session";

function defaultWindow(): ReportWindowInput {
  const to = new Date();
  const from = new Date(to);
  from.setDate(from.getDate() - 29);
  return { from: localDay(from), to: localDay(to), dealerId: null };
}

/** Funnel counts and the audited case-events export. Counts only: rates, durations and cohorts are deliberately P1. */
export function TexReportsPage() {
  const me = useMe();
  const staff = isTexStaff(me.data);
  const opsAdmin = me.data?.tex_role === "ops_admin";
  const [draft, setDraft] = useState<ReportWindowInput>(defaultWindow);
  const [applied, setApplied] = useState<ReportWindowInput>(draft);
  const report = useFunnelReport(applied, staff);
  const dealers = useDealers();

  if (me.data === null) return <Navigate to="/sign-in" />;
  if (!me.data) return <p className="text-sm text-neutral-500">Loading…</p>;
  if (!staff)
    return <Alert tone="error">Reports are for Tex staff only.</Alert>;

  const windowValid = draft.from <= draft.to;

  return (
    <section className="space-y-6">
      <PageHeader
        eyebrow="Tex"
        title="Reports"
        description="How many requests sit at each stage, how many entered each stage in the window, and why files closed. Counts only — no rates, durations or cohorts yet."
      />

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (windowValid) setApplied(draft);
        }}
        className="flex flex-wrap items-end gap-3"
      >
        <Field label="From">
          <Input
            type="date"
            value={draft.from}
            max={draft.to}
            onChange={(e) => setDraft({ ...draft, from: e.target.value })}
            required
          />
        </Field>
        <Field label="To" hint="Inclusive.">
          <Input
            type="date"
            value={draft.to}
            min={draft.from}
            onChange={(e) => setDraft({ ...draft, to: e.target.value })}
            required
          />
        </Field>
        <Field label="Dealer">
          <Select
            value={draft.dealerId ?? ""}
            onChange={(e) =>
              setDraft({ ...draft, dealerId: e.target.value || null })
            }
          >
            <option value="">All dealers</option>
            {(dealers.data?.dealers ?? []).map((d) => (
              <option key={d.id} value={d.id}>
                {d.trading_name ?? d.legal_name}
              </option>
            ))}
          </Select>
        </Field>
        <Button type="submit" disabled={!windowValid}>
          Apply
        </Button>
      </form>
      {!windowValid && (
        <Alert tone="error">The window must start on or before it ends.</Alert>
      )}

      {report.isPending && (
        <p className="text-sm text-neutral-500">Loading…</p>
      )}
      {report.isError && (
        <Alert tone="error">{errorMessage(report.error)}</Alert>
      )}
      {report.data && <Funnel report={report.data} />}

      {opsAdmin ? (
        <ExportCard window={applied} />
      ) : (
        <Card title="Export">
          <p className="text-sm text-neutral-500">
            The case-events CSV is an ops-admin export; each download is
            audited.
          </p>
        </Card>
      )}
    </section>
  );
}

function Funnel({ report }: { report: FunnelReport }) {
  const total = report.stages.reduce((n, s) => n + s.current, 0);
  return (
    <div className="space-y-6">
      <Card
        title="Funnel"
        actions={
          <span className="text-xs text-neutral-500">
            {formatDate(report.window.from)} – {formatDate(report.window.to)} ·
            generated {formatDate(report.generated_at)}
          </span>
        }
      >
        <table className="w-full text-sm">
          <thead className="text-left font-display text-[11px] font-bold uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="py-1 pr-4">Stage</th>
              <th className="py-1 pr-4 text-right">In stage now</th>
              <th className="py-1 text-right">Entered in window</th>
            </tr>
          </thead>
          <tbody>
            {report.stages.map((s) => (
              <tr
                key={s.stage}
                className="border-t border-neutral-100 transition-colors hover:bg-neutral-50/60"
              >
                <td className="py-2 pr-4 font-medium">
                  {STAGE_LABELS[s.stage]}
                </td>
                <td className="py-2 pr-4 text-right">
                  <Mono>{s.current}</Mono>
                </td>
                <td className="py-2 text-right">
                  <Mono>{s.entered_in_window}</Mono>
                </td>
              </tr>
            ))}
            <tr className="border-t border-neutral-200 font-medium">
              <td className="py-2 pr-4">All cases</td>
              <td className="py-2 pr-4 text-right">
                <Mono>{total}</Mono>
              </td>
              <td className="py-2 text-right text-xs text-neutral-500">
                created <Mono>{report.created_in_window}</Mono> · closed{" "}
                <Mono>{report.closed_in_window}</Mono>
              </td>
            </tr>
          </tbody>
        </table>
      </Card>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <Card title="Close reasons in window">
          <CountList
            rows={report.close_reasons.map((r) => ({
              label: CLOSE_REASON_LABELS[r.close_reason],
              count: r.count,
            }))}
            empty="No cases closed in this window."
          />
        </Card>
        <Card title="Out-of-scope referrals in window">
          <CountList
            rows={report.referrals.map((r) => ({
              label: REFERRAL_CATEGORY_LABELS[r.referral_category],
              count: r.count,
            }))}
            empty="Nothing referred out in this window."
          />
          <p className="mt-3 text-xs text-neutral-500">
            What buyers asked for that Tex does not arrange — the signal for
            which product to build or partner on next.
          </p>
        </Card>
      </div>
    </div>
  );
}

function CountList({
  rows,
  empty,
}: {
  rows: { label: string; count: number }[];
  empty: string;
}) {
  if (rows.length === 0)
    return <p className="text-sm text-neutral-500">{empty}</p>;
  return (
    <dl>
      {rows.map((r) => (
        <div
          key={r.label}
          className="flex justify-between gap-4 py-1 text-sm"
        >
          <dt className="text-neutral-700">{r.label}</dt>
          <dd className="font-medium">
            <Mono>{r.count}</Mono>
          </dd>
        </div>
      ))}
    </dl>
  );
}

function ExportCard({ window }: { window: ReportWindowInput }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<CsvDownload | null>(null);
  const [error, setError] = useState<string | null>(null);
  return (
    <Card title="Export case events">
      <p className="text-sm text-neutral-500">
        Every timeline event in the window as CSV (reference, stage moves,
        actor type, summary). No application answers, terms or metadata; the
        download itself is written to the audit trail. A session older than
        the step-up limit is asked to sign in again first.
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Button
          type="button"
          variant="secondary"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            setError(null);
            downloadCaseEventsCsv(window)
              .then(setResult)
              .catch((err: unknown) => setError(errorMessage(err)))
              .finally(() => setBusy(false));
          }}
        >
          {busy ? "Preparing…" : "Download CSV"}
        </Button>
        {result && (
          <span className="text-xs text-neutral-500">
            <Mono>{result.filename}</Mono> · {result.rows} rows
            {result.truncated && " · truncated at the export cap; narrow the window"}
          </span>
        )}
      </div>
      {error && (
        <div className="mt-3">
          <Alert tone="error">{error}</Alert>
        </div>
      )}
    </Card>
  );
}
